/**
 * SubContractorHub connection flow — paste API key → Test Connection →
 * Save and continue.
 *
 * SECURITY:
 * - Input uses type="password" (NFR9).
 * - The plaintext API key is held in component-local state only and is
 *   wiped immediately after a successful save (or panel close).
 * - No "show key" toggle for V1.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import {
  useTestSubContractorHubConnection,
  useConnectSubContractorHub,
} from '@/hooks/integrations/useSubContractorHubConnection';

interface ConnectionTabProps {
  onClose: () => void;
}

interface TestSucceededState {
  externalOrgName: string | null;
  customFieldCount: number;
}

export function ConnectionTab({ onClose }: ConnectionTabProps) {
  const [apiKey, setApiKey] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestSucceededState | null>(null);

  const { toast } = useToast();
  const testMutation = useTestSubContractorHubConnection();
  const connectMutation = useConnectSubContractorHub();

  const isBusy = testMutation.isPending || connectMutation.isPending;
  const canTest = apiKey.trim().length > 0 && !isBusy;
  const canSave = testResult !== null && !isBusy;

  const handleApiKeyChange = (value: string) => {
    setApiKey(value);
    // Reset prior test result if the user edits the key — they need to retest.
    if (testResult !== null) setTestResult(null);
    if (errorMessage !== null) setErrorMessage(null);
  };

  const handleTest = async () => {
    setErrorMessage(null);
    setTestResult(null);
    try {
      const response = await testMutation.mutateAsync({ apiKey });
      if (response.error) {
        setErrorMessage(
          response.error.code === 'auth_failed'
            ? 'Invalid API key. Please verify and try again.'
            : 'Could not reach SubContractorHub. Try again in a moment.',
        );
        return;
      }
      setTestResult(response.data);
    } catch {
      setErrorMessage('Could not reach SubContractorHub. Try again in a moment.');
    }
  };

  const handleSave = async () => {
    if (!testResult) return;
    setErrorMessage(null);
    try {
      const response = await connectMutation.mutateAsync({
        apiKey,
        externalOrgName: testResult.externalOrgName,
      });
      if (response.error) {
        if (response.error.code === 'integration_already_exists') {
          setErrorMessage('An integration of this type already exists for this tenant.');
        } else if (response.error.code === 'auth_failed') {
          setErrorMessage('Invalid API key. Please verify and try again.');
          setTestResult(null);
        } else if (response.error.code === 'connection_test_failed') {
          setErrorMessage('Could not reach SubContractorHub. Try again in a moment.');
          setTestResult(null);
        } else {
          setErrorMessage('Could not finalize integration. Please try again.');
        }
        return;
      }
      // Wipe key from memory immediately on success.
      setApiKey('');
      setTestResult(null);
      toast({
        title: 'Connected',
        description: response.data.externalOrgName
          ? `Connected to ${response.data.externalOrgName}.`
          : 'Connection saved.',
      });
      onClose();
    } catch {
      setErrorMessage('Could not finalize integration. Please try again.');
    }
  };

  const handleCancelAfterTest = () => {
    setApiKey('');
    setTestResult(null);
    setErrorMessage(null);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          Paste your SubContractorHub API key. We&apos;ll verify it&apos;s valid before you save —
          no data is imported in this step.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="sch-api-key">SubContractorHub API key</Label>
        <Input
          id="sch-api-key"
          type="password"
          autoComplete="off"
          spellCheck={false}
          placeholder="Paste your API key"
          value={apiKey}
          onChange={(e) => handleApiKeyChange(e.target.value)}
          disabled={isBusy || testResult !== null}
        />
      </div>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>Connection error</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      {!testResult && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button onClick={handleTest} disabled={!canTest}>
            {testMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Test Connection
          </Button>
        </div>
      )}

      {testResult && (
        <div className="space-y-4">
          <Alert>
            <AlertTitle>Connection successful</AlertTitle>
            <AlertDescription>
              {testResult.externalOrgName
                ? `Connected to ${testResult.externalOrgName}.`
                : 'Connected. Organization name not available — you can confirm and save.'}
              {' '}
              Discovered {testResult.customFieldCount} custom fields.
            </AlertDescription>
          </Alert>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleCancelAfterTest} disabled={isBusy}>
              Use a different key
            </Button>
            <Button onClick={handleSave} disabled={!canSave}>
              {connectMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save and continue
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
